import { useEffect, useState } from "react";
import { Edit2, Loader2, Search, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiGet } from "@/lib/apiAdmin";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const inp = "w-full h-10 rounded-xl px-3 text-sm bg-white/[0.03] border border-white/10 text-white focus:border-sky-500 outline-none";

const EditModal = ({ c, onClose, onSaved }) => {
  const [f, setF] = useState({ name: c.name || "", email: c.email || "", phone: c.phone || "", notes: c.notes || "" });
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    api.patch(`/admin/customers/${c.user_id}`, f).then(() => { toast.success("Customer updated"); onSaved(); onClose(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Save failed")).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-3" data-testid="customer-edit-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        <h3 className="font-['Outfit'] text-xl text-white">Edit customer</h3>
        {["name", "email", "phone"].map((k) => (
          <input key={k} className={inp} placeholder={k[0].toUpperCase() + k.slice(1)} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`customer-edit-${k}`} />
        ))}
        <textarea rows={3} className={`${inp} h-auto py-2`} placeholder="Notes (gate code, pets, preferences…)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="customer-edit-notes" />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="h-10 px-4 text-sm text-gray-400">Cancel</button>
          <button onClick={save} disabled={busy} className="h-10 px-6 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-sm flex items-center gap-2" data-testid="customer-edit-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ c, onEdit, onDelete }) => (
  <div className="p-4 flex flex-wrap items-center gap-3" data-testid={`customer-row-${c.user_id}`}>
    {c.picture ? <img src={c.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-400"><Users className="w-4 h-4" /></div>}
    <div className="flex-1 min-w-[160px]">
      <p className="text-white">{c.name || "—"}</p>
      <p className="text-xs text-gray-500 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
      {c.notes && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{c.notes}</p>}
    </div>
    <div className="text-right text-xs text-gray-500 w-28"><p className="text-sky-400 font-semibold text-sm">{fmt(c.total_spent)}</p>{c.bookings_count} visit{c.bookings_count === 1 ? "" : "s"} · {c.last_booking || "—"}</div>
    <button onClick={onEdit} className="p-2 text-sky-400 hover:bg-sky-500/10 rounded-lg" data-testid={`customer-edit-${c.user_id}`}><Edit2 className="w-4 h-4" /></button>
    <button onClick={onDelete} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`customer-delete-${c.user_id}`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

export const CustomersManager = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => apiGet("/admin/customers").then((d) => setRows(d.customers)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const remove = (c) => window.confirm(`Delete ${c.name || c.email}? Their past bookings stay in your records.`)
    && api.delete(`/admin/customers/${c.user_id}`).then(() => { toast.success("Customer deleted"); load(); }).catch((e) => toast.error(e?.response?.data?.detail || "Delete failed"));
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-sky-500" />;
  const t = q.toLowerCase();
  const shown = rows.filter((c) => !t || `${c.name} ${c.email} ${c.phone || ""}`.toLowerCase().includes(t));
  return (
    <div className="space-y-3" data-testid="customers-manager">
      <div className="relative max-w-sm"><Search className="w-4 h-4 text-gray-500 absolute left-3 top-3" /><input className={`${inp} pl-9`} placeholder="Search name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" /></div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
        {shown.map((c) => <Row key={c.user_id} c={c} onEdit={() => setEditing(c)} onDelete={() => remove(c)} />)}
        {shown.length === 0 && <p className="text-center text-gray-500 py-8">No customers found</p>}
      </div>
      {editing && <EditModal c={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
};
